import React, { useRef, useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const formStartedAt = useRef(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
        body: JSON.stringify({ ...formData, formStartedAt: formStartedAt.current }),
      });

      const text = await response.text();
      if (!text) throw new Error('Empty response from server');
      const data = JSON.parse(text);
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to send message');

      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '', website: '' });
      formStartedAt.current = Date.now();
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm";

  return (
    <section id="contact" className="bg-slate-950 py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="h-px w-8 bg-gold-400" />
            <span className="section-label text-gold-400">Get In Touch</span>
            <div className="h-px w-8 bg-gold-400" />
          </div>
          <h2 className="section-title text-white mb-4">Get a Free Glass Railing Quote in Seattle</h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Serving Seattle, WA and the Greater Vancouver, BC area. Contact us for a free on-site consultation and quote.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 bg-white rounded-2xl p-8 shadow-2xl">
            <h3 className="font-display text-xl font-bold text-slate-900 mb-6">Send Us a Message</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required minLength={2} maxLength={80} autoComplete="name" placeholder="Your full name" className={inputClass} />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Phone</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} maxLength={30} autoComplete="tel" placeholder="(xxx) xxx-xxxx" className={inputClass} />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="email" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Email *</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required maxLength={254} autoComplete="email" placeholder="your@email.com" className={inputClass} />
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Message *</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange} required minLength={10} maxLength={2000} rows={4} placeholder="Tell us about your project..." className={inputClass + ' resize-none'} />
            </div>

            {/* Real visitors never see this field. Bots that auto-fill it are silently discarded server-side. */}
            <div className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Leave this field empty</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center gap-2 py-4 text-base disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</>
              ) : (
                <><Send className="w-4 h-4" />Send Message</>
              )}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 flex items-center gap-2 bg-green-50 text-green-700 p-4 rounded-xl text-sm font-medium">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                Thanks—your inquiry was securely received. We'll get back to you within 24 hours.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 text-red-700 p-4 rounded-xl text-sm font-medium">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errorMessage || 'Failed to send. Please try again.'}
              </div>
            )}
          </form>

          {/* Info cards */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {[
              {
                icon: <Phone className="w-5 h-5" />,
                title: 'Call Us',
                content: (
                  <>
                    <a href="tel:3014335323" className="block text-slate-300 hover:text-gold-400 transition-colors">(301) 433-5323</a>
                    <a href="tel:3014335323" className="block text-slate-300 hover:text-gold-400 transition-colors">(301) 433-5323</a>
                  </>
                ),
              },
              {
                icon: <Mail className="w-5 h-5" />,
                title: 'Email Us',
                content: <a href="mailto:info@primeviewglass.com" className="text-slate-300 hover:text-gold-400 transition-colors break-all">info@primeviewglass.com</a>,
              },
              {
                icon: <MapPin className="w-5 h-5" />,
                title: 'Visit Us',
                content: (
                  <>
                    <p className="text-slate-300">Serving Seattle, WA & Greater Vancouver, BC</p>
                    <p className="text-slate-500 text-xs mt-1">Mon–Fri: 8AM–6PM | Sat: 9AM–2PM</p>
                  </>
                ),
              },
            ].map(({ icon, title, content }) => (
              <div key={title} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-xl bg-brand-600/20 flex items-center justify-center text-brand-400 flex-shrink-0">
                  {icon}
                </div>
                <div>
                  <p className="text-white font-semibold text-sm mb-1">{title}</p>
                  <div className="text-sm">{content}</div>
                </div>
              </div>
            ))}

            <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-6 mt-2">
              <p className="text-white font-bold font-display mb-1">Quick Response Guarantee</p>
              <p className="text-brand-100 text-sm leading-relaxed">
                We respond to all inquiries within 24 hours. For immediate help, call us directly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
